import {Image} from '@volkswagen-onehub/components-core';
import * as React from 'react';
import {useMeasure} from 'react-use';
import Div from '../components/Div.styled';
import DescriptionPage from '../components/HeadlineSubHeadlineText';
import Sidebar from '../components/Sidebar';
import useMediaQuery from '../hooks/useMediaQuery';
import {JSONMARKDOWN, VWCARMODELLABELS} from '../types/results';
import {VWCarModel} from '../types/surveyResponse';
import {replaceText} from '../utils/stringUtils';
import EVMap from './map/evMap';
import {config} from '../environment';
import {useSendSurveyResultAnalytic} from '../hooks/useSendResultSurveyAnalytic';
import ArIcon from '../ev-ampol-amp-charge/ar-icon';

export interface IBannerProps {
  name: string;
  model: VWCarModel;
  body: string;
  body_two?: string;
  body_three?: string;
  cta_one: string;
  cta_two: string;
  hero_sub_title: string;
  hero_title_line_1: string;
  hero_title_line_2: string;
  image: string;
  image_background: string;
  post_code: string;
  isHomeCharging: boolean;
  discover_ampol_experience_url?: string;
}

export default function ResultSectionTwo(props: IBannerProps) {
  const {
    name,
    model,
    body,
    body_two,
    body_three,
    cta_one,
    hero_sub_title,
    hero_title_line_1,
    hero_title_line_2,
    image,
    image_background,
    post_code,
    isHomeCharging,
    discover_ampol_experience_url = '/default/EV_AMPOL_AMP_CHARGE/app',
  } = props;

  const {isDesktop, isMobile, isTablet} = useMediaQuery();
  const [imageRef, {height: imageHeight}] = useMeasure<HTMLDivElement>();
  const [openSidebar, setOpenSidebar] = React.useState(false);
  const [pois, setPois] = React.useState(0);
  const descriptions = [body, body_two, body_three].filter(Boolean);
  const mergedDescriptions = descriptions.join(' ');
  const {
    handleCTAClick,
    handleMapLoad,
    handleMapSearchLocation,
    handleSelectChargingStation,
  } = useSendSurveyResultAnalytic(model, post_code);

  const descriptionReplacements = {
    [JSONMARKDOWN.name]: name,
    [JSONMARKDOWN.model]:
      VWCARMODELLABELS[model as VWCarModel].replace('&', 'or') ?? '',
  };
  const divHeight = isDesktop ? 1300 : isTablet ? 943 : 536;

  const paddingOfImageInsideContainer = (divHeight - imageHeight) / 2;

  function onViewMap() {
    const currentUrl = window.location.href;
    const pathSegments = currentUrl.split('/');
    pathSegments.splice(-2);
    pathSegments.push('ev-map.html');
    const newUrl = pathSegments.join('/');
    window.open(newUrl, '_blank');
  }

  const handleHomeChargingCTAClick = () => {
    window.location.href = discover_ampol_experience_url;
  };

  const handleMapChargingCTAClick = () => {
    config.EVMAP_ENABLED ? setOpenSidebar(true) : onViewMap();
  };

  return (
    <Div customStyles={{position: 'relative'}}>
      {config.EVMAP_ENABLED && (
        <Sidebar
          active={openSidebar}
          title="Nice one!"
          subTitle={`I found ${pois} public charging station(s) in your local area`}
          onClose={() => setOpenSidebar(false)}
        >
          <EVMap
            postCode={post_code}
            setPois={setPois}
            onLoad={handleMapLoad}
            onSearchLocation={handleMapSearchLocation}
            onSelectChargingStation={handleSelectChargingStation}
            onCTAClick={handleCTAClick}
          />
        </Sidebar>
      )}
      {isHomeCharging ? (
        <Div
          customStyles={{
            maxWidth: '700px',
            marginRight: 'auto',
            marginLeft: 'auto',
          }}
        >
          <Image
            src={image}
            width={'100%'}
            height={'auto'}
            alt="result section 2 image"
            style={{maxWidth: '100%'}}
          />
        </Div>
      ) : (
        <Div customStyles={{marginTop: isMobile ? '90px' : '113px'}}>
          <Div
            customStyles={{
              position: 'relative',
              backgroundImage: `url(${image_background})`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: isMobile ? 'cover' : '100% 100%',
              textAlign: 'center',
              transformOrigin: 'center bottom',
              height: divHeight,
              display: 'flex',
              width: '100%',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Div
              ref={imageRef}
              maxWidth={isMobile ? '268px' : isTablet ? '308px' : '100%'}
            >
              <Image
                src={image}
                width={'100%'}
                height={'auto'}
                alt="result section 2 image"
                style={{maxWidth: '100%'}}
              />
            </Div>
          </Div>
        </Div>
      )}

      <Div
        margin="10px 0 0"
        customStyles={{
          left: 0,
          width: '100%',
          ...(!isHomeCharging && {
            transform: `translateY(${-paddingOfImageInsideContainer + 100}px)`,
          }),
        }}
      >
        <DescriptionPage
          subTitle={hero_sub_title}
          title={hero_title_line_1}
          titleStrong={hero_title_line_2}
          descriptions={replaceText(
            mergedDescriptions,
            descriptionReplacements,
          )}
          {...(isHomeCharging
            ? {
                CTAButtonPrimaryTitle: cta_one,
                icon: <ArIcon style={{marginRight: '2px'}} />,
                handleCTAPrimary: handleHomeChargingCTAClick,
              }
            : {
                CTAButtonTitle: cta_one || 'View local charging map',
                handleCTA: handleMapChargingCTAClick,
              })}
        />
      </Div>
    </Div>
  );
}
